import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  Paper,
  Skeleton,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Chip,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import apiService from '../../services/api';
import TimeRangeFilter from '../common/TimeRangeFilter';
import useTimeRangeParam from '../common/useTimeRangeParam';
import { TimeRangeValue, resolveRangeYmd } from '../common/timeRange';
import { safeMetric } from '../../utils/safeMetric';

const PAGE_COLORS: Record<string, string> = {
  'home':             '#1A5F7A',
  'how-it-works':     '#D4AC0D',
  'for-families':     '#7D1935',
  'pricing':          '#10B981',
  'help':             '#5D6D7E',
  'about':            '#4c1d95',
  'privacy-policy':   '#9CA3AF',
  'terms-of-service': '#9CA3AF',
  'stories':          '#146152',
  'blog':             '#0ea5e9',
  'support':          '#ef4444',
  'download':         '#f59e0b',
  'gift':             '#ec4899',
  'other':            '#d1d5db',
};

const PRIMARY_PAGES = ['home', 'how-it-works', 'stories', 'for-families', 'pricing', 'help', 'about', 'privacy-policy', 'terms-of-service'];
const CHART_PAGES = ['home', 'how-it-works', 'stories', 'for-families', 'pricing', 'help', 'about'];

/**
 * This endpoint speaks numeric-string day counts (`range=30`), not the canonical
 * `30d` tokens. Its `mtd` / `365` / `all` cases are still served — the backend
 * keeps them — but they are now reached by picking the span under Custom rather
 * than by three one-off options that existed on this screen alone.
 */
const RANGE_TOKENS: Record<string, string> = {
  today: 'today',
  '3d': '3',
  '7d': '7',
  '14d': '14',
  '30d': '30',
  '90d': '90',
};

interface PageSummary {
  page: string;
  label: string;
  views: number;
  mobile: number;
  tablet: number;
  desktop: number;
  avg_time_sec: number | null;
  avg_scroll: number | null;
}

interface SummaryData {
  total: number;
  avg_time_sec: number | null;
  avg_scroll: number | null;
  form_completion_pct: number | null;
  pages: PageSummary[];
}

interface TimeseriesRow {
  bucket: string;
  [page: string]: string | number;
}

interface CtaRow {
  cta_id: string;
  cta_label: string | null;
  clicks: number;
  unique_clicks: number;
  pct_of_total: number;
}

function fmtTime(sec: number | null): string {
  if (sec == null) return '—';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}m ${String(s).padStart(2, '0')}s`;
}

/** `range=…` plus `start`&`end` when the operator picked a custom span. */
function rangeQS(value: TimeRangeValue): string {
  if (value.type === 'custom') {
    const { from, to } = resolveRangeYmd(value);
    return `range=custom&start=${from}&end=${to}`;
  }
  return `range=${RANGE_TOKENS[value.type] ?? '30'}`;
}

// ─── V2 top KPI card ─────────────────────────────────────────────────────────

interface KpiCardProps {
  label: string;
  value: React.ReactNode;
  sub: React.ReactNode;
  subColor?: string;
  topColor: string;
  isNew?: boolean;
  loading: boolean;
}

const KpiCard: React.FC<KpiCardProps> = ({ label, value, sub, subColor = '#6B7280', topColor, isNew, loading }) => (
  <Paper sx={{ p: 2.5, borderRadius: '14px', border: '1px solid #E5E7EB', borderTop: `3px solid ${topColor}`, boxShadow: 'none' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
      <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.7px', color: '#6B7280' }}>
        {label}
      </Typography>
      {isNew && (
        <Box sx={{ px: 0.8, py: 0.15, bgcolor: '#D4AC0D', borderRadius: '4px', fontFamily: "'Montserrat', sans-serif", fontSize: '9px', fontWeight: 800, color: '#fff', letterSpacing: '0.5px', lineHeight: 1.6 }}>
          NEW
        </Box>
      )}
    </Box>
    {loading ? (
      <Skeleton variant="text" width={80} height={40} />
    ) : (
      <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '28px', fontWeight: 800, color: '#111827', lineHeight: 1.1, mb: 0.3 }}>
        {value}
      </Typography>
    )}
    {!loading && (
      <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '11px', color: subColor, fontWeight: 500 }}>
        {sub}
      </Typography>
    )}
  </Paper>
);

// ─── V2 per-page stat card ────────────────────────────────────────────────────

interface PageCardProps {
  label: string;
  views: number;
  timeSec: number | null;
  scroll: number | null;
  color: string;
  loading: boolean;
  merged?: boolean;
}

const PageCard: React.FC<PageCardProps> = ({ label, views, timeSec, scroll, color, loading, merged }) => (
  <Paper sx={{ p: 2, borderRadius: '12px', border: '1px solid #E5E7EB', borderTop: `3px solid ${color}`, boxShadow: 'none' }}>
    <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', color: '#6B7280', mb: 0.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
      {label}
    </Typography>
    {loading ? (
      <Skeleton variant="text" width={40} height={32} />
    ) : (
      <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '26px', fontWeight: 800, color: '#111827', lineHeight: 1.1, mb: 0.3 }}>
        {safeMetric(views)}
      </Typography>
    )}
    {!loading && (
      <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '10px', color: '#9CA3AF' }}>
        {merged
          ? (timeSec != null ? `${fmtTime(timeSec)} avg` : 'no data yet')
          : (timeSec != null || scroll != null)
            ? `${fmtTime(timeSec)}${scroll != null ? ` · ${scroll}% scroll` : ''}`
            : 'no engagement data yet'}
      </Typography>
    )}
  </Paper>
);

// ─── Main component ───────────────────────────────────────────────────────────

const WebsiteAnalyticsPage: React.FC = () => {
  // Three INDEPENDENT windows on one page, so each hook namespaces its own URL
  // keys (`summary_range`, `chart_range`, `cta_range`) and cannot clobber the
  // other two.
  const [summaryRange, setSummaryRange] = useTimeRangeParam({ type: '30d' }, { prefix: 'summary' });
  const [chartRange, setChartRange] = useTimeRangeParam({ type: '30d' }, { prefix: 'chart' });
  const [ctaRange, setCtaRange] = useTimeRangeParam({ type: '30d' }, { prefix: 'cta' });

  const [selectedPages, setSelectedPages] = useState<string[]>(CHART_PAGES.slice(0, 5));
  const [allPages, setAllPages] = useState<{ page: string; label: string }[]>(
    PRIMARY_PAGES.map(p => ({ page: p, label: p }))
  );

  const [summary, setSummary] = useState<SummaryData | null>(null);
  const [timeseries, setTimeseries] = useState<TimeseriesRow[]>([]);
  const [ctaLeaderboard, setCtaLeaderboard] = useState<CtaRow[]>([]);

  const [loadingSummary, setLoadingSummary] = useState(true);
  const [loadingChart, setLoadingChart] = useState(true);
  const [loadingCta, setLoadingCta] = useState(true);

  useEffect(() => {
    apiService.get('/api/admin/website/pages').then(res => {
      if (res.data) setAllPages(res.data as { page: string; label: string }[]);
    }).catch(() => {});
  }, []);

  const loadSummary = useCallback(async (range: TimeRangeValue) => {
    setLoadingSummary(true);
    try {
      const res = await apiService.get(`/api/admin/website/summary?${rangeQS(range)}`);
      if (res.data) setSummary(res.data as SummaryData);
    } catch {}
    setLoadingSummary(false);
  }, []);

  const loadChart = useCallback(async (range: TimeRangeValue, pages: string[]) => {
    setLoadingChart(true);
    try {
      const res = await apiService.get(`/api/admin/website/timeseries?${rangeQS(range)}&pages=${pages.join(',')}`);
      if (res.data) {
        const d = res.data as { pages: string[]; rows: TimeseriesRow[] };
        const rows = d.rows.map(row => ({
          ...row,
          label: new Date(row.bucket).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        }));
        setTimeseries(rows);
      }
    } catch {}
    setLoadingChart(false);
  }, []);

  const loadCta = useCallback(async (range: TimeRangeValue) => {
    setLoadingCta(true);
    try {
      const res = await apiService.get(`/api/admin/website/cta-leaderboard?${rangeQS(range)}`);
      if (res.data) setCtaLeaderboard(res.data as CtaRow[]);
    } catch {}
    setLoadingCta(false);
  }, []);

  useEffect(() => { loadSummary(summaryRange); }, [summaryRange, loadSummary]);
  useEffect(() => { loadChart(chartRange, selectedPages); }, [chartRange, selectedPages, loadChart]);
  useEffect(() => { loadCta(ctaRange); }, [ctaRange, loadCta]);

  const pageMap = new Map((summary?.pages || []).map(s => [s.page, s]));

  const pageLabel = (page: string) => {
    const found = allPages.find(p => p.page === page);
    return found ? found.label : page;
  };

  const privacyRow = pageMap.get('privacy-policy');
  const termsRow   = pageMap.get('terms-of-service');
  const privTermsViews = (privacyRow?.views || 0) + (termsRow?.views || 0);
  const privTermsTime  = privacyRow?.avg_time_sec != null || termsRow?.avg_time_sec != null
    ? Math.round(((privacyRow?.avg_time_sec || 0) + (termsRow?.avg_time_sec || 0)) / 2) : null;

  const displayCards = [
    { page: 'home',         label: 'Home',           merged: false },
    { page: 'how-it-works', label: 'How It Works',   merged: false },
    { page: 'stories',      label: 'Stories',         merged: false },
    { page: 'for-families', label: 'For Families',    merged: false },
    { page: 'pricing',      label: 'Pricing',         merged: false },
    { page: 'help',         label: 'Help',            merged: false },
    { page: 'about',        label: 'About',           merged: false },
    { page: 'privacy+terms',label: 'Privacy + Terms', merged: true  },
  ];

  const hasEngagement = summary?.avg_time_sec != null || summary?.avg_scroll != null;
  const maxCtaClicks = ctaLeaderboard.length > 0 ? Math.max(...ctaLeaderboard.map(r => r.clicks)) : 1;

  return (
    <Box sx={{ p: 3, bgcolor: '#F9FAFB', minHeight: '100vh' }}>

      {/* Breadcrumb */}
      <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.8px', color: '#6B7280', mb: 1 }}>
        GROWTH &amp; ACQUISITION{' '}
        <Box component="span" sx={{ mx: 0.5 }}>&rarr;</Box>{' '}
        <Box component="span" sx={{ color: '#1A5F7A' }}>WEBSITE TRAFFIC</Box>
      </Typography>

      {/* Header */}
      <Box sx={{ mb: 2.5 }}>
        <Typography sx={{ fontFamily: "'Playfair Display', serif", fontSize: '30px', fontWeight: 700, color: '#111827', lineHeight: 1.2 }}>
          Website Traffic
        </Typography>
        <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '13px', color: '#6B7280', mt: 0.5 }}>
          First-party page view analytics + behavioral engagement · trylifescribe.com · No cookies required
        </Typography>
      </Box>

      {/* v2 enhancements banner */}
      <Alert
        icon={<Box component="span" sx={{ fontSize: '14px' }}>★</Box>}
        severity="warning"
        sx={{
          mb: 2.5,
          bgcolor: 'rgba(212,172,13,0.08)',
          borderLeft: '4px solid #D4AC0D',
          '& .MuiAlert-icon': { color: '#D4AC0D', alignItems: 'center' },
          fontFamily: "'Montserrat', sans-serif",
          fontSize: '12px',
          color: '#374151',
        }}
      >
        <Box component="span" sx={{ fontWeight: 700, color: '#92680a' }}>v2 enhancements:</Box>{' '}
        Engagement quality (time-on-page, scroll depth, form completion), CTA leaderboard, and form funnel — all first-party, no third-party trackers.
        {!hasEngagement && <Box component="span" sx={{ color: '#9CA3AF', ml: 1 }}>Engagement data will appear as new visitors arrive.</Box>}
      </Alert>

      {/* Summary tiles section header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
        <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', color: '#6B7280' }}>
          Page View Summary
        </Typography>
        <TimeRangeFilter
          value={summaryRange}
          onChange={setSummaryRange}
          onRefresh={() => loadSummary(summaryRange)}
          refreshing={loadingSummary}
        />
      </Box>

      {/* 4 KPI Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, mb: 2 }}>
        <KpiCard
          label="Total Page Views"
          value={loadingSummary ? '—' : safeMetric(summary?.total || 0)}
          sub={`Across ${PRIMARY_PAGES.length} pages`}
          topColor="#1A5F7A"
          loading={loadingSummary}
        />
        <KpiCard
          label="Avg Time on Page"
          value={summary?.avg_time_sec != null ? fmtTime(summary.avg_time_sec) : '—'}
          sub={summary?.avg_time_sec != null ? 'Industry avg: 0m 54s' : 'No data yet — tracking active'}
          subColor={summary?.avg_time_sec != null ? '#146152' : '#9CA3AF'}
          topColor="#D4AC0D"
          isNew
          loading={loadingSummary}
        />
        <KpiCard
          label="Avg Scroll Depth"
          value={summary?.avg_scroll != null ? `${summary.avg_scroll}%` : '—'}
          sub={summary?.avg_scroll != null
            ? summary.avg_scroll >= 60 ? 'Strong content engagement' : summary.avg_scroll >= 40 ? 'Moderate engagement' : 'Low engagement'
            : 'No data yet — tracking active'}
          subColor={summary?.avg_scroll != null ? '#374151' : '#9CA3AF'}
          topColor="#146152"
          isNew
          loading={loadingSummary}
        />
        <KpiCard
          label="Form Completion Rate"
          value={summary?.form_completion_pct != null ? `${summary.form_completion_pct}%` : '—'}
          sub={summary?.form_completion_pct != null ? 'Target: >50%' : 'Visits to /download vs intent pages'}
          subColor={summary?.form_completion_pct != null
            ? summary.form_completion_pct >= 50 ? '#146152' : '#7D1935'
            : '#9CA3AF'}
          topColor="#7D1935"
          isNew
          loading={loadingSummary}
        />
      </Box>

      {/* Per-page cards: 4 columns × 2 rows */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', mb: 3 }}>
        {displayCards.map(card => {
          if (card.merged) {
            return (
              <PageCard
                key="privacy+terms"
                label={card.label}
                views={privTermsViews}
                timeSec={privTermsTime}
                scroll={null}
                color={PAGE_COLORS['privacy-policy']}
                loading={loadingSummary}
                merged
              />
            );
          }
          const row = pageMap.get(card.page);
          return (
            <PageCard
              key={card.page}
              label={card.label}
              views={row?.views || 0}
              timeSec={row?.avg_time_sec ?? null}
              scroll={row?.avg_scroll ?? null}
              color={PAGE_COLORS[card.page] || '#9CA3AF'}
              loading={loadingSummary}
            />
          );
        })}
      </Box>

      {/* Timeseries chart */}
      <Paper sx={{ p: 2.5, borderRadius: '14px', border: '1px solid #E5E7EB', boxShadow: 'none', mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '13px', fontWeight: 700, color: '#111827' }}>
            Daily Page Views by Page
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <TimeRangeFilter
              value={chartRange}
              onChange={setChartRange}
              onRefresh={() => loadChart(chartRange, selectedPages)}
              refreshing={loadingChart}
            />
            <Select
              multiple
              size="small"
              value={selectedPages}
              onChange={e => {
                const val = e.target.value as string[];
                if (val.length > 0) setSelectedPages(val);
              }}
              input={<OutlinedInput size="small" />}
              renderValue={selected => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as string[]).map(p => (
                    <Chip
                      key={p}
                      label={pageLabel(p)}
                      size="small"
                      sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '10px', height: 18, bgcolor: `${PAGE_COLORS[p] || '#9CA3AF'}20`, color: PAGE_COLORS[p] || '#6B7280' }}
                    />
                  ))}
                </Box>
              )}
              sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '12px', minWidth: 180, maxWidth: 280 }}
              MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
            >
              {allPages.map(p => (
                <MenuItem key={p.page} value={p.page} dense>
                  <Checkbox checked={selectedPages.includes(p.page)} size="small" sx={{ color: PAGE_COLORS[p.page] || '#9CA3AF' }} />
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: PAGE_COLORS[p.page] || '#9CA3AF', mr: 1, flexShrink: 0 }} />
                  <ListItemText primary={p.label} primaryTypographyProps={{ sx: { fontFamily: "'Montserrat', sans-serif", fontSize: '13px' } }} />
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>

        {loadingChart ? (
          <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 2 }} />
        ) : timeseries.length === 0 ? (
          <Box sx={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '14px', color: '#9CA3AF' }}>
              No page views yet
            </Typography>
          </Box>
        ) : (
          <div style={{ height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={timeseries as any[]}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#6B7280' }} />
                <YAxis tick={{ fontSize: 10, fill: '#6B7280' }} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ fontFamily: "'Montserrat', sans-serif", fontSize: '12px' }}
                  formatter={(val: number, name: string) => [val.toLocaleString(), pageLabel(name)]}
                />
                <Legend
                  wrapperStyle={{ fontFamily: "'Montserrat', sans-serif", fontSize: '11px' }}
                  formatter={name => pageLabel(name as string)}
                />
                {selectedPages.map(page => (
                  <Line
                    key={page}
                    type="monotone"
                    dataKey={page}
                    stroke={PAGE_COLORS[page] || '#9CA3AF'}
                    strokeWidth={2}
                    dot={false}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </Paper>

      {/* CTA Leaderboard */}
      <Paper sx={{ p: 2.5, borderRadius: '14px', border: '1px solid #E5E7EB', boxShadow: 'none' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '13px', fontWeight: 700, color: '#111827' }}>
              CTA Leaderboard
            </Typography>
            <Box sx={{ px: 0.8, py: 0.15, bgcolor: '#D4AC0D', borderRadius: '4px', fontFamily: "'Montserrat', sans-serif", fontSize: '9px', fontWeight: 800, color: '#fff', letterSpacing: '0.5px', lineHeight: 1.6 }}>
              NEW
            </Box>
          </Box>
          <TimeRangeFilter
            value={ctaRange}
            onChange={setCtaRange}
            onRefresh={() => loadCta(ctaRange)}
            refreshing={loadingCta}
          />
        </Box>

        {loadingCta ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} variant="rectangular" height={36} sx={{ borderRadius: 1 }} />)}
          </Box>
        ) : ctaLeaderboard.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '14px', color: '#9CA3AF', mb: 1 }}>
              No CTA clicks recorded yet
            </Typography>
            <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '12px', color: '#D1D5DB' }}>
              Clicks on elements with <code style={{ fontSize: '11px', background: '#F3F4F6', padding: '1px 4px', borderRadius: 3 }}>data-cta-id</code> will appear here
            </Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px', color: '#6B7280', borderBottom: '1px solid #E5E7EB', pb: 1 }}>CTA</TableCell>
                <TableCell align="right" sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px', color: '#6B7280', borderBottom: '1px solid #E5E7EB', pb: 1 }}>Clicks</TableCell>
                <TableCell align="right" sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px', color: '#6B7280', borderBottom: '1px solid #E5E7EB', pb: 1 }}>Unique</TableCell>
                <TableCell sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px', color: '#6B7280', borderBottom: '1px solid #E5E7EB', pb: 1, minWidth: 160 }}>% of Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ctaLeaderboard.map((row, idx) => (
                <TableRow key={row.cta_id} sx={{ '&:last-child td': { border: 0 } }}>
                  <TableCell sx={{ borderBottom: idx < ctaLeaderboard.length - 1 ? '1px solid #F3F4F6' : 'none', py: 1 }}>
                    <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '13px', fontWeight: 600, color: '#111827' }}>
                      {row.cta_label || row.cta_id}
                    </Typography>
                    {row.cta_label && (
                      <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '10px', color: '#9CA3AF' }}>
                        {row.cta_id}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ borderBottom: idx < ctaLeaderboard.length - 1 ? '1px solid #F3F4F6' : 'none', py: 1 }}>
                    <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '14px', fontWeight: 700, color: '#111827' }}>
                      {row.clicks.toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ borderBottom: idx < ctaLeaderboard.length - 1 ? '1px solid #F3F4F6' : 'none', py: 1 }}>
                    <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '13px', color: '#374151' }}>
                      {row.unique_clicks.toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ borderBottom: idx < ctaLeaderboard.length - 1 ? '1px solid #F3F4F6' : 'none', py: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ flex: 1, height: 6, bgcolor: '#F3F4F6', borderRadius: 3, overflow: 'hidden' }}>
                        <Box sx={{ height: '100%', width: `${(row.clicks / maxCtaClicks) * 100}%`, bgcolor: '#1A5F7A', borderRadius: 3 }} />
                      </Box>
                      <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '12px', color: '#374151', fontWeight: 600, minWidth: 36, textAlign: 'right' }}>
                        {Number(row.pct_of_total).toFixed(1)}%
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

    </Box>
  );
};

export default WebsiteAnalyticsPage;
